import { type FunctionComponent, type ReactNode, useContext } from 'react';

import { Button, PopoverNext, Spinner } from '@blueprintjs/core';
import { InfoSignIcon } from '@blueprintjs/icons';
import { TaskStatusesContext } from 'App';
import { TaskStatusDtoStateEnum } from 'generated-sources/openapi';

import i18n from 'i18n/i18n';
import { testAttribute } from 'util/Util';

const TASK_PROCESSED_COUNT_LABEL_KEYS: Record<string, string> = {
  DUNNING_CRON: 'task-status.processed-count.events',
  MANUAL_EVALUATION: 'task-status.processed-count.contracts',
  BULK_DUNNING_LEVEL_RESET: 'task-status.processed-count.contracts',
  RULESET_REPLACEMENT: 'task-status.processed-count.contracts',
};

const TASK_NAME_TRANSLATION_KEYS: Record<string, string> = {
  DUNNING_CRON: 'task-status.task-names.DUNNING_CRON',
  MANUAL_EVALUATION: 'task-status.task-names.MANUAL_EVALUATION',
  BULK_DUNNING_LEVEL_RESET: 'task-status.task-names.BULK_DUNNING_LEVEL_RESET',
  RULESET_REPLACEMENT: 'task-status.task-names.RULESET_REPLACEMENT',
  MANUAL_RESET_TO_PREVIOUS_LEVEL: 'task-status.task-names.MANUAL_RESET_TO_PREVIOUS_LEVEL',
};

function formatTimestamp(timestamp?: Date) {
  if (!timestamp) return i18n.t('task-status.unavailable-value');
  return `${timestamp.toLocaleDateString(i18n.language)} ${timestamp.toLocaleTimeString(i18n.language)}`;
}

function stateLabel(state: string) {
  const translationKey = `task-status.states.${state}`;
  return i18n.exists(translationKey) ? i18n.t(translationKey) : i18n.t('task-status.states.UNKNOWN');
}

function processedCountLabel(taskId: string) {
  return i18n.t(TASK_PROCESSED_COUNT_LABEL_KEYS[taskId] ?? 'task-status.processed-count.records');
}

function taskName(taskId: string, fallback: string) {
  const translationKey = TASK_NAME_TRANSLATION_KEYS[taskId];
  return translationKey ? i18n.t(translationKey) : fallback;
}

const TaskStatusInfo: FunctionComponent = () => {
  const taskStatuses = useContext(TaskStatusesContext);

  let content: ReactNode;
  if (taskStatuses.status === 'loading') {
    content = (
      <div className="task-status-info__message">
        <Spinner size={16} />
        <span>{i18n.t('task-status.loading')}</span>
      </div>
    );
  } else if (taskStatuses.status === 'unavailable') {
    content = <div className="task-status-info__message">{i18n.t('task-status.unavailable')}</div>;
  } else {
    content = (
      <div className="task-status-info__entries">
        {taskStatuses.tasks.map(task => (
          <section className="task-status-info__entry" key={task.id}>
            <strong>{taskName(task.id, task.name)}</strong>
            <div>
              {i18n.t('task-status.state')}: {stateLabel(task.state)}
            </div>
            <div>
              {i18n.t('task-status.started-at')}: {formatTimestamp(task.startedAt)}
            </div>
            {task.state === TaskStatusDtoStateEnum.Completed && task.finishedAt ? (
              <div>
                {i18n.t('task-status.finished-at')}: {formatTimestamp(task.finishedAt)}
              </div>
            ) : null}
            <div>
              {processedCountLabel(task.id)}: {task.processedCount ?? 0}
            </div>
            {task.state === TaskStatusDtoStateEnum.Failed && task.failureMessage ? (
              <div>
                {i18n.t('task-status.failure-message')}: {task.failureMessage}
              </div>
            ) : null}
          </section>
        ))}
      </div>
    );
  }

  return (
    <PopoverNext content={content} interactionKind="hover" placement="bottom-end">
      <Button
        aria-label={i18n.t('task-status.title')}
        className="task-status-info__trigger"
        data-test-id={testAttribute('tsti', 'nav.task-status')}
        icon={<InfoSignIcon />}
        variant="minimal">
        {i18n.t('task-status.title')}
      </Button>
    </PopoverNext>
  );
};

export default TaskStatusInfo;
